import ENV from "@/utils/env";
import { api } from "@/lib/api";
import { Project } from "./types/project.type";
import { PaginatedReq, PaginatedResp } from "../types/paginated.type";

/* Request Types */
export interface GetProjectsReq extends PaginatedReq {
  query?: string;
  sortBy?: "asc" | "desc";
  orderBy?: "name" | "updatedAt";
  view?: "all" | "private" | "public" | "archived";
}

export interface CreateProjectReq {
  name: string;
  visibility: string | boolean;
  thumbnailName: string;
}

export interface UpdateProjectReq {
  id: string;
  values: {
    name: string;
    visibility: string;
    isArchived?: boolean;
    thumbnailName: string;
  };
}

/* Response Types */
export interface GetProjectsResp extends PaginatedResp {
  projects: Array<Project>;
}

export interface CreateProjectResp {
  project: Project;
}

export interface GetProjectResp {
  project: Project;
}

export interface UpdateProjectResp {
  id: string;
  name: string;
}

export interface DeleteProjectResp {
  id: string;
}

/*
 * getProjects endpoint
 *
 * This endpoint is used to get all projects for an account.
 */
export const getProjects = async (req: GetProjectsReq) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects`, { params: req });
  return resp.data as GetProjectsResp;
};

/*
 * createProject endpoint
 *
 * This endpoint is used to create a new project for an account.
 */
export const createProject = async (values: CreateProjectReq) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects`, values);
  return resp.data as CreateProjectResp;
};

/*
 * getProject endpoint
 *
 * This endpoint is used to get a specific project based on its ID.
 */
export const getProject = async (id: string) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${id}`);
  return resp.data as GetProjectResp;
};

/*
 * updateProject endpoint
 *
 * This endpoint is used to update a specific project based on its ID and the values to update.
 */
export const updateProject = async ({ id, values }: UpdateProjectReq) => {
  const resp = await api.put(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${id}`, values);
  return resp.data as UpdateProjectResp;
};

/*
 * deleteProject endpoint
 *
 * This endpoint is used to delete a specific project based on its ID.
 */
export const deleteProject = async (projectId: string) => {
  const resp = await api.delete(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}`);
  return resp.data as DeleteProjectResp;
};
